import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

export default function Toast({
  type = "info",
  title,
  message,
  onClose,
  onClick,
  avatarUrl = "",
  avatarInitials = "",
  meta = "",
  duration = 9000,
}) {
  const remainingRef = React.useRef(duration);
  const startedAtRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const [paused, setPaused] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);

  const icons = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
  };

  const Icon = icons[type] || Info;

  function closeWithAnimation() {
    clearTimer();
    setLeaving(true);

    window.setTimeout(() => {
      onClose?.();
    }, 260);
  }

  function handleClick() {
    if (onClick) {
      onClick();
      closeWithAnimation();
    }
  }

  function clearTimer() {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function startTimer() {
    if (duration === null) return;

    startedAtRef.current = Date.now();

    timerRef.current = window.setTimeout(() => {
      closeWithAnimation();
    }, remainingRef.current);
  }

  function pauseTimer() {
    if (duration === null) return;

    clearTimer();

    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);

    setPaused(true);
  }

  function resumeTimer() {
    if (duration === null) return;

    setPaused(false);
    startTimer();
  }

  React.useEffect(() => {
    startTimer();

    return clearTimer;
  }, []);

  return (
    <div
      className={`toast toast--${type} ${onClick ? "toast--clickable" : ""} ${
        leaving ? "is-leaving" : ""
      } ${paused ? "is-paused" : ""}`}
      style={{ "--toast-progress-duration": `${duration}ms` }}
      onClick={handleClick}
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      role="status"
    >
      {avatarUrl || avatarInitials ? (
        <div className="toast__avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" />
          ) : (
            <span className="toast__avatar-fallback">{avatarInitials}</span>
          )}
        </div>
      ) : (
        <div className="toast__icon">
          <Icon size={24} />
        </div>
      )}

      <div className="toast__content">
        <strong>{title}</strong>
        {message ? <p>{message}</p> : null}
        {meta ? <small className="toast__meta">{meta}</small> : null}
      </div>

      <button
        type="button"
        className="toast__close"
        onClick={(event) => {
          event.stopPropagation();
          closeWithAnimation();
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
